import { steppedProgressStateLabel, type SteppedProgressState } from "@imminently/decisively-ui/SteppedProgress";
import type { ResourceInspection } from "@/features/adapters/model";
import type { AdapterBlockStatus } from "./stepTypes";
import { getAdapterWorkflowStepState, type AdapterWorkflowStepStatus } from "./persistence";
import { getAdapterStepResource } from "./stepResource";
import type { AdapterWizardStep, AdapterWizardStepAccess } from "./wizard";

export type AdapterStepProgressState = SteppedProgressState;

interface ResolveAdapterStepProgressInput {
  complete: boolean;
  /** Part of the step is done. */
  inProgress?: boolean;
  disabled?: boolean;
  persistedState?: AdapterStepProgressState;
  issue?: "warning" | "destructive";
}

export const resolveAdapterStepProgress = ({
  complete,
  inProgress,
  disabled,
  persistedState,
  issue,
}: ResolveAdapterStepProgressInput): AdapterStepProgressState => {
  if (disabled) return "not-started";
  if (issue) return issue;
  if (complete) return "success";
  if (persistedState === "warning" || persistedState === "destructive") return persistedState;
  if (persistedState === "in-progress" || inProgress) return "in-progress";
  return "not-started";
};

export const adapterStepProgressLabel: Record<AdapterStepProgressState, string> = steppedProgressStateLabel;

/**
 * A step the setup has moved past: one that's unfinished (in progress, or not started but open) or complete with
 * something still left to do, while a later step is complete. It shows a warning rather than in progress, since
 * it was left behind rather than being worked on. What "complete" means doesn't change.
 */
export const flagStepsLeftBehind = <TSegment extends { key: string; state: AdapterStepProgressState; disabled?: boolean }>(
  segments: TSegment[],
  outstanding: Record<string, boolean> = {},
): TSegment[] => {
  const lastComplete = segments.map((segment) => segment.state).lastIndexOf("success");
  return segments.map((segment, index) => {
    if (index >= lastComplete || segment.disabled) return segment;
    const unfinished = segment.state === "in-progress" || segment.state === "not-started";
    const leftOver = segment.state === "success" && Boolean(outstanding[segment.key]);
    return unfinished || leftOver ? { ...segment, state: "warning" as const } : segment;
  });
};

/** The card and guide must describe the same step with the same access and issue rules. */
export const buildAdapterProgressSegments = ({
  steps,
  inspections,
  statuses,
  outstanding,
  access,
  completion,
  automaticCompletion,
  manualCompletions,
  stepStatus,
  errorsByKey,
  currentStepKey,
  connectionTestError,
  started = true,
}: {
  steps: AdapterWizardStep[];
  inspections: ResourceInspection[];
  /** Each step's status from its blocks; without it (saved progress) the step's resource decides. */
  statuses?: Record<string, AdapterBlockStatus>;
  /** Steps with something left to do that doesn't hold them up (see flagStepsLeftBehind). */
  outstanding?: Record<string, boolean>;
  access: AdapterWizardStepAccess[];
  completion: Record<string, boolean>;
  automaticCompletion: Record<string, boolean>;
  manualCompletions: Record<string, boolean>;
  stepStatus?: AdapterWorkflowStepStatus[];
  errorsByKey?: Record<string, unknown[]>;
  currentStepKey?: string;
  connectionTestError?: string;
  started?: boolean;
}) => flagStepsLeftBehind(steps.map((step, index) => {
  const resource = getAdapterStepResource(inspections, step);
  const disabled = !access[index]?.accessible;
  const hasAttemptError = Boolean(errorsByKey?.[step.key]?.length);
  const deferNameConflict = resource?.ownership === "conflict" &&
    (resource.kind === "mapping" || resource.kind === "deltaMapping") &&
    !hasAttemptError;
  const hasError = hasAttemptError ||
    (step.key === currentStepKey && Boolean(connectionTestError)) ||
    resource?.state === "error" || resource?.state === "conflict" || statuses?.[step.key] === "destructive";
  const needsAttention = statuses ? statuses[step.key] === "warning" : resource?.state === "incomplete";
  const manuallyComplete = Boolean(manualCompletions[step.key] && !automaticCompletion[step.key]);
  // Before the live resources are in (no statuses) the saved state stands in. After, the state is worked out afresh
  // from what's been done: being on or visiting a step doesn't start it. A step with no live status of its own (its
  // resources failed to load) keeps what was saved.
  const liveStatus = statuses?.[step.key];
  const savedState = statuses && liveStatus !== undefined ? undefined : getAdapterWorkflowStepState(stepStatus, step.key);
  const persistedState = deferNameConflict ? undefined : savedState;
  return {
    key: step.key,
    label: step.label,
    successTone: manuallyComplete || step.requiresConfiguration ? ("success" as const) : ("neutral" as const),
    successIcon: manuallyComplete ? ("warning" as const) : ("check" as const),
    manualConfirmed: manuallyComplete,
    disabled,
    disabledReason: access[index]?.reason,
    state: started ? resolveAdapterStepProgress({
      disabled,
      complete: Boolean(completion[step.key]),
      inProgress: statuses?.[step.key] === "in-progress",
      persistedState,
      issue: (hasError && !deferNameConflict) || (manuallyComplete && persistedState === "destructive")
        ? "destructive"
        : manuallyComplete ? undefined : needsAttention ? "warning" : undefined,
    }) : ("not-started" as const),
  };
}), outstanding);

/**
 * The step states to save, or undefined when there's nothing new. Until the live resources load the states shown
 * are the saved ones, so nothing is saved; once they load, the recalculated states are saved whenever they differ, so
 * the next load starts from them.
 */
export const getStepStatusToSave = ({
  live,
  saved,
  segments,
}: {
  live: boolean;
  saved: AdapterWorkflowStepStatus[];
  segments: Array<{ key: string; state: AdapterStepProgressState }>;
}): AdapterWorkflowStepStatus[] | undefined => {
  if (!live || segments.length === 0) return undefined;
  const resolved = segments.map((segment) => ({ id: segment.key, state: segment.state }));
  const unchanged = saved.length === resolved.length &&
    resolved.every((status) => getAdapterWorkflowStepState(saved, status.id) === status.state);
  return unchanged ? undefined : resolved;
};
